import React from "react";
import { Box, Paper, Typography, Snackbar, Alert } from "@mui/material";

import DevIdeFileListPanel from "./DevIdeFileListPanel";
import DevIdeEditorPanel from "./DevIdeEditorPanel";
import DevIdeToolbar from "../components/DevIdeToolbar";
import DevIdeModuleActions from "../components/DevIdeModuleActions";
import AgentWorkflowPanel from "../components/AgentWorkflowPanel";
import DevIdeSqlList from "../components/DevIdeSqlList";
import NewModuleDialog from "../components/NewModuleDialog";
import NewAppDialog from "../components/NewAppDialog";
import NewFileDialog from "../components/NewFileDialog";
import NewSqlDialog from "../components/NewSqlDialog";
import SqlEditorDialog from "../components/SqlEditorDialog";
import ImportZipDialog from "../components/ImportZipDialog";
import DownloadModuleZipDialog from "../components/DownloadModuleZipDialog";

import useDevIdePanelState from "./useDevIdePanelState";

/**
 * DevIdePanel
 *
 * Minimal "IDE" view for sandbox:
 * - Left: file list (from planner_file_map via /api/dev-ide/files)
 * - Right: file content viewer + copy/paste for GPT + save with backup
 * - Extra: module dropdown + button to download a ZIP of all files in a module
 */

const DevIdePanel = ({
  currentUsername,
  selectedProjectId: sharedSelectedProjectId,
  setSelectedProjectId: setSharedSelectedProjectId,
  importContext,
  onImportContextHandled,
}) => {
  const state = useDevIdePanelState(currentUsername, {
    selectedProjectId: sharedSelectedProjectId,
    setSelectedProjectId: setSharedSelectedProjectId,
    importContext,
    onImportContextHandled,
  });

  const {
    // NEW: projects
    projects,
    projectsLoading,
    projectsError,
    selectedProjectId,
    setSelectedProjectId,

    // core state
    filesLoading,
    filesError,
    filteredFiles,
    selectedFileId,
    selectedFileMeta,
    fileContent,
    fileOriginalContent,
    fileLoading,
    fileError,
    gptPasteContent,
    changeSummary,
    saveLoading,
    saveMessage,
    saveErrorMessage, // NEW
    saveErrorOpen, // NEW
    setSaveErrorOpen, // NEW
    diffText,
    diffLoading,
    diffError,
    diffLineCount,
    backendActionLoading,
    frontendActionLoading,
    actionMessage,
    workflowStatus,
    workflowStatusLoading,
    workflowStatusError,
    markWorkflowStep,
    resetWorkflowStatus,
    changes,
    activeChange,
    changeFiles,
    changeFilterMode,
    revertWholeChangeLoading,
    // changeError, // currently unused but kept for future use
    fileHistory,
    fileHistoryLoading,
    fileHistoryError,
    modules,
    selectedModule,
    filterText,

    // rules panel (CHG-037)
    rulesMarkdown,
    rulesLoading,
    rulesError,

    // setters for toolbar / filters
    setSelectedModule,
    setFilterText,
    setChangeFilterMode,

    // handlers: changes + filters
    handleSelectActiveChange,
    handleCreateNewChange,
    handleRevertWholeChange,

    // file selection + editor controls
    handleSelectFile,
    handleCopyFullForGPT,
    handleCopySelectionForGPT,
    handleApplyGptPaste,
    handlePreviewDiff,
    handleSaveFile,

    // sandbox actions
    handleRestartSandboxBackend,
    handleRebuildSandboxFrontend,
    handleRunBackendScan,
    handleRunFrontendScan,
    handleDescribeFile,

    // history
    handleLoadFileHistory,
    handleRevertFile,

    // SQL + module list
    sqlFiles,
    sqlLoading,
    sqlError,
    handleDownloadModuleZip,
    handleOpenDownloadZipWithDocs,
    handleCloseDownloadZipWithDocs,
    handleAgentExportPackage,
    handleCopyExportAgentHandoffSummary,

    // new app dialog
    newAppOpen,
    newAppForm,
    newAppPreview,
    newAppError,
    newAppPreviewLoading,
    newAppCreateLoading,

    // new module dialog
    newModuleOpen,
    newModuleName,
    newModuleParent, // <-- NEW
    newModuleBackend,
    newModuleFrontend,
    newModuleError,
    newModuleLoading,
    handleOpenNewModule,
    handleCloseNewModule,
    handleCreateModule,

    // new file dialog
    newFileOpen,
    newFileModule,
    newFileSide,
    newFileFolder,
    newFileName,
    newFileType,
    newFileError,
    newFileLoading,
    handleOpenNewFile,
    handleCloseNewFile,
    handleCreateFile,

    // new SQL dialog
    newSqlOpen,
    newSqlSequence,
    newSqlDescription,
    newSqlError,
    newSqlLoading,
    handleOpenNewSql,
    handleCloseNewSql,
    handleCreateSqlFile,

    // SQL editor dialog
    sqlEditorOpen,
    selectedSqlFile,
    sqlEditorContent,
    sqlEditorLoading,
    sqlEditorError,
    sqlSaveLoading,
    sqlSaveMessage,
    handleOpenSqlEditorForFile,
    handleCloseSqlEditorDialog,
    handleSaveSqlEditor,

    // ZIP import
    importZipOpen,
    importZipIncludeDocs,
    downloadZipDialogOpen,
    handleOpenImportZip,
    handleOpenImportZipWithDocs,
    handleCloseImportZip,
    handleImportedSaved,
    handleOpenNewApp,
    handleCloseNewApp,
    handleChangeNewAppField,
    handlePreviewNewApp,
    handleCreateNewApp,
  } = state;

  const handleCloseSaveError = (_, reason) => {
    if (reason === "clickaway") return;
    setSaveErrorOpen(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        minHeight: "600px",
      }}
    >
      <DevIdeToolbar
        backendActionLoading={backendActionLoading}
        frontendActionLoading={frontendActionLoading}
        actionMessage={actionMessage}
        selectedModule={selectedModule}
        modules={modules}
        setSelectedModule={setSelectedModule}
        filterText={filterText}
        setFilterText={setFilterText}
        changes={changes}
        activeChange={activeChange}
        handleSelectActiveChange={handleSelectActiveChange}
        changeFilterMode={changeFilterMode}
        setChangeFilterMode={setChangeFilterMode}
        handleCreateNewChange={handleCreateNewChange}
        handleRevertWholeChange={handleRevertWholeChange}
        revertWholeChangeLoading={revertWholeChangeLoading}
        handleRestartSandboxBackend={handleRestartSandboxBackend}
        handleRebuildSandboxFrontend={handleRebuildSandboxFrontend}
        handleAgentExportPackage={handleAgentExportPackage}

        // NEW: projects
        projects={projects}
        projectsLoading={projectsLoading}
        projectsError={projectsError}
        selectedProjectId={selectedProjectId}
        setSelectedProjectId={setSelectedProjectId}
        handleOpenNewApp={handleOpenNewApp}
      />

      <AgentWorkflowPanel
        activeChange={activeChange}
        actionMessage={actionMessage}
        workflowStatus={workflowStatus}
        workflowStatusLoading={workflowStatusLoading}
        workflowStatusError={workflowStatusError}
        onResetWorkflowStatus={resetWorkflowStatus}
        onWorkflowStep={markWorkflowStep}
        onAgentExportPackage={handleAgentExportPackage}
        onCopyExportAgentHandoffSummary={handleCopyExportAgentHandoffSummary}
        onOpenImportZipWithDocs={handleOpenImportZipWithDocs}
      />

      <NewAppDialog
        open={newAppOpen}
        form={newAppForm}
        preview={newAppPreview}
        error={newAppError}
        previewLoading={newAppPreviewLoading}
        createLoading={newAppCreateLoading}
        onClose={handleCloseNewApp}
        onChange={handleChangeNewAppField}
        onPreview={handlePreviewNewApp}
        onCreate={handleCreateNewApp}
      />

      <Box sx={{ display: "flex", gap: 2, flex: 1, minHeight: 0 }}>
        {/* LEFT PANEL */}
        <Paper
          elevation={2}
          sx={{
            width: "32%",
            display: "flex",
            flexDirection: "column",
            maxHeight: "100%",
          }}
        >
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
            <Typography variant="h6" gutterBottom>
              Dev IDE - Files
            </Typography>

            <DevIdeModuleActions
              modules={modules}
              selectedModule={selectedModule}
              onDownloadZip={handleDownloadModuleZip}
              onDownloadZipWithDocs={handleOpenDownloadZipWithDocs}
              onAgentExportPackage={handleAgentExportPackage}
              onCopyExportAgentHandoffSummary={handleCopyExportAgentHandoffSummary}
              onOpenImportZip={handleOpenImportZip}
              onOpenImportZipWithDocs={handleOpenImportZipWithDocs}
              onOpenNewModule={handleOpenNewModule}
              onOpenNewFile={handleOpenNewFile}
              onOpenNewSql={handleOpenNewSql}
              onRunBackendScan={handleRunBackendScan}
              onRunFrontendScan={handleRunFrontendScan}
              backendActionLoading={backendActionLoading}
              frontendActionLoading={frontendActionLoading}
              activeChange={activeChange}
              sqlError={sqlError}
              sqlLoading={sqlLoading}
              sqlFiles={sqlFiles}
            />

            <DevIdeSqlList
              activeChange={activeChange}
              sqlFiles={sqlFiles}
              onOpenSqlEditor={handleOpenSqlEditorForFile}
            />
          </Box>

          <Box sx={{ flex: 1, minHeight: 0 }}>
            <DevIdeFileListPanel
              filesLoading={filesLoading}
              filesError={filesError}
              filteredFiles={filteredFiles}
              selectedFileId={selectedFileId}
              onSelectFile={handleSelectFile}
              activeChangeId={
                activeChange && activeChange.id ? activeChange.id : null
              }
              changeFiles={changeFiles}
            />
          </Box>
        </Paper>

        {/* RIGHT PANEL */}
        <Paper
          elevation={2}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            maxHeight: "100%",
          }}
        >
          <DevIdeEditorPanel
            selectedFileId={selectedFileId}
            selectedFileMeta={selectedFileMeta}
            fileContent={fileContent}
            fileOriginalContent={fileOriginalContent}
            fileLoading={fileLoading}
            fileError={fileError}
            gptPasteContent={gptPasteContent}
            changeSummary={changeSummary}
            saveLoading={saveLoading}
            saveMessage={saveMessage}
            diffText={diffText}
            diffLoading={diffLoading}
            diffError={diffError}
            diffLineCount={diffLineCount}
            onCopyFullForGPT={handleCopyFullForGPT}
            onCopySelectionForGPT={handleCopySelectionForGPT}
            onChangeFileContent={(value) => state.setFileContent(value)}
            onChangeGptPasteContent={(value) => state.setGptPasteContent(value)}
            onApplyGptPaste={handleApplyGptPaste}
            onChangeSummary={(value) => state.setChangeSummary(value)}
            onPreviewDiff={handlePreviewDiff}
            onSaveFile={handleSaveFile}
            onRevertFile={handleRevertFile}
            onDescribeFile={handleDescribeFile}
            activeChange={activeChange}
            onLoadFileHistory={handleLoadFileHistory}
            fileHistory={fileHistory}
            fileHistoryLoading={fileHistoryLoading}
            fileHistoryError={fileHistoryError}
            // rules panel props
            rulesMarkdown={rulesMarkdown}
            rulesLoading={rulesLoading}
            rulesError={rulesError}
          />
        </Paper>

        {/* NEW: Create Module Dialog */}
        <NewModuleDialog
          open={newModuleOpen}
          newModuleName={newModuleName}
          newModuleParent={newModuleParent} // <-- NEW
          newModuleBackend={newModuleBackend}
          newModuleFrontend={newModuleFrontend}
          newModuleError={newModuleError}
          newModuleLoading={newModuleLoading}
          onClose={handleCloseNewModule}
          onChangeModuleName={state.setNewModuleName}
          onChangeParentModule={state.setNewModuleParent} // <-- NEW
          onToggleBackend={(checked) => state.setNewModuleBackend(checked)}
          onToggleFrontend={(checked) => state.setNewModuleFrontend(checked)}
          onCreateModule={handleCreateModule}
        />

        {/* NEW: Create File Dialog */}
        <NewFileDialog
          open={newFileOpen}
          modules={modules}
          newFileModule={newFileModule}
          newFileSide={newFileSide}
          newFileFolder={newFileFolder}
          newFileName={newFileName}
          newFileType={newFileType}
          newFileError={newFileError}
          newFileLoading={newFileLoading}
          onClose={handleCloseNewFile}
          onChangeFileModule={state.setNewFileModule}
          onChangeFileSide={state.setNewFileSide}
          onChangeFileFolder={state.setNewFileFolder}
          onChangeFileName={state.setNewFileName}
          onChangeFileType={state.setNewFileType}
          onCreateFile={handleCreateFile}
        />

        {/* NEW: Create SQL Dialog (CHG-047) */}
        <NewSqlDialog
          open={newSqlOpen}
          newSqlSequence={newSqlSequence}
          newSqlDescription={newSqlDescription}
          newSqlError={newSqlError}
          newSqlLoading={newSqlLoading}
          onClose={handleCloseNewSql}
          onChangeSqlSequence={state.setNewSqlSequence}
          onChangeSqlDescription={state.setNewSqlDescription}
          onCreateSqlFile={handleCreateSqlFile}
        />

        {/* NEW: SQL Editor Dialog (CHG-047) */}
        <SqlEditorDialog
          open={sqlEditorOpen}
          selectedSqlFile={selectedSqlFile}
          sqlEditorContent={sqlEditorContent}
          sqlEditorLoading={sqlEditorLoading}
          sqlEditorError={sqlEditorError}
          sqlSaveLoading={sqlSaveLoading}
          sqlSaveMessage={sqlSaveMessage}
          activeChange={activeChange}
          onClose={handleCloseSqlEditorDialog}
          onChangeSqlEditorContent={state.setSqlEditorContent}
          onSaveSqlEditor={handleSaveSqlEditor}
        />

        <DownloadModuleZipDialog
          open={downloadZipDialogOpen}
          onClose={handleCloseDownloadZipWithDocs}
          modules={modules}
          selectedModule={selectedModule}
          selectedProjectId={selectedProjectId}
          activeChange={activeChange}
          onDownload={handleDownloadModuleZip}
        />

        <ImportZipDialog
          open={importZipOpen}
          onClose={handleCloseImportZip}
          selectedProjectId={selectedProjectId}
          activeChange={activeChange}
          currentUsername={currentUsername}
          onImportedSaved={handleImportedSaved}
          onWorkflowStep={markWorkflowStep}
          includeDocuments={importZipIncludeDocs}
        />
      </Box>

      {/* NEW: Save error snackbar */}
      <Snackbar
        open={Boolean(saveErrorOpen && saveErrorMessage)}
        autoHideDuration={6000}
        onClose={handleCloseSaveError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSaveError}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {saveErrorMessage || "Error saving file."}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DevIdePanel;
